import type { Play } from '@carboncopyco/types';
import {
  Container,
  Separator,
  SkeletonRowLoading,
  TextBase,
  TooltipBubble,
  UserBasicInfo,
} from '@carboncopyco/ui';
import { type ReactNode, useEffect } from 'react';
import { type Col, FixedTable } from '../../../FixedTable';
import { Screenshot } from '../../../Screenshot';
import { CreatedOnTooltip } from '../../CreatedOnTooltip';
import { EmptyState } from '../../EmptyState';
import { handleSinglePlural } from '../../utils';
import { usePresentationsContext } from '../PresentationsContext';
import { ArchivedRowButtons } from './ArchivedRowButtons';
import { Controls } from './Controls';

const cols: Col<Play>[] = [
  {
    alignment: 'left',
    title: 'Opportunity',
    width: 220,
    maxContentWidth: 200,
    render: (presentation) => {
      return (
        <div className="flex items-center justify-between gap-3 w-full">
          <div className="w-14 h-9 shrink-0">
            <Screenshot
              image={presentation?.image}
              alt="demo"
              rounded="sm"
              isPlaybook
            />
          </div>
          <div className="flex flex-col flex-grow overflow-hidden">
            <TooltipBubble
              placement="top-start"
              title={presentation?.forClient}
              disabled={presentation?.forClient?.length < 18}
            >
              <div className="overflow-hidden truncate">
                <TextBase
                  ellipsis
                  color="text-ds-natural-600"
                  label={presentation?.forClient}
                />
              </div>
            </TooltipBubble>
          </div>
        </div>
      );
    },
  },
  {
    title: 'Date',
    alignment: 'left',
    width: 120,
    maxContentWidth: 116,
    render: ({ createTime }) => <CreatedOnTooltip dateTime={createTime} />,
  },
  {
    alignment: 'left',
    title: 'Creator',
    width: 150,
    maxContentWidth: 130,
    render: ({ creator }) => (
      <div className="flex items-center">
        <UserBasicInfo
          size="small"
          user={creator}
          textColor="text-ds-natural-600"
          truncateWidth={110}
          tooltip={creator.name.length > 15}
        />
      </div>
    ),
  },

  {
    alignment: 'right',
    title: 'Presented',
    width: 85,
    render: ({ totalPresentationViews }) => (
      <TooltipBubble
        title={handleSinglePlural(totalPresentationViews, 'Presented')}
        placement="top"
      >
        <TextBase
          label={totalPresentationViews || '-'}
          color="text-ds-natural-600"
        />
      </TooltipBubble>
    ),
  },
  {
    alignment: 'right',
    title: '',
    width: 390,
    render: (presentation) => (
      <ArchivedRowButtons presentation={presentation} />
    ),
  },
];

export const ArchivedPresentations = () => {
  const {
    fetchArgs,
    fetchFirstBatch,
    fetchNextBatch,
    isLoadingBatch,
    isEmpty,
    error,
    isLoading,
    presentations,
  } = usePresentationsContext();

  useEffect(() => {
    fetchFirstBatch(fetchArgs);
  }, [fetchArgs, fetchFirstBatch]);

  const RowWrapper = ({ children }: { children?: ReactNode }) => (
    <div className="flex flex-col">
      <div className="py-4">{children}</div>
      <Separator horizontal />
    </div>
  );

  if (isEmpty) {
    return (
      <EmptyState
        title="No archived presentations"
        description="No presentations have been archived yet."
      />
    );
  }

  return (
    <>
      <Controls isArchive />
      <div className="py-5">
        <FixedTable<Play>
          error={error}
          data={presentations ?? []}
          cols={cols}
          isLoading={isLoading}
          handleFetchNextBatch={() => fetchNextBatch(fetchArgs)}
          isLoadingBatch={isLoadingBatch}
          sideSpacing
          keyName="id"
          infiniteListMaxHeight={800}
          rowWrapper={RowWrapper}
          loader={
            <Container>
              <SkeletonRowLoading
                medium
                itemsCount={5}
              />
            </Container>
          }
        />
      </div>
    </>
  );
};
